import React, { useState } from 'react';
import { Plan, PricingVariant } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import { getVariantPrice } from '@wix/pricing-plans-utils';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { Image, ImageLoadingBehaviorOptions, ImageResizeOptions } from '@wix/wix-ui-tpa/cssVars';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { ButtonDemo } from '../../../../../constants/settings-events';
import { demoImages } from '../../../../../fixtures';
import { alignmentName } from '../../../../../utils/alignment';
import { getPlanBenefits, planImageToCoverImage } from '../../../../../utils/plan';
import { VariantCategoryNameById } from '../../../../../utils/variant-categories';
import settingsParams, { ButtonStyle } from '../../../settingsParams';
import stylesParams, { type TopButton } from '../../../stylesParams';
import { BUTTON_ALIGNMENT_STYLES_PARAM_BY_BUTTON_STYLE } from '../Plan/constants';
import { Footer } from '../Plan/Footer';
import { FreeTrial } from '../Plan/FreeTrial';
import { PlanButtons } from '../Plan/PlanButtons';
import { PlanName } from '../Plan/PlanName';
import { Price } from '../Plan/Price';
import { Recurrence } from '../Plan/Recurrence';
import { Ribbon } from '../Plan/Ribbon';
import { SetupFee } from '../Plan/SetupFee';
import { Tagline } from '../Plan/Tagline';
import { useShadowStyle } from '../Plan/useShadowStyle';
import { Validity } from '../Plan/Validity';
import { st, classes, vars } from './PlanCard.st.css';
import { VariantToggle } from './VariantToggle';

export interface PlanCardProps {
  plan: Plan;
  variants: PricingVariant[];
  variantCategoryNameById: VariantCategoryNameById;
  isVariantSelectionEnabled: boolean;
  highlighted: boolean;
  buttonDemo: ButtonDemo;
  recurringPlansExist: boolean;
  freeTrialDaysExist: boolean;
  validityCyclesExist: boolean;
  setupFeesExist: boolean;
  taglinesExist: boolean;
  anyBenefits: boolean;
  expandMobileBenefits: boolean;
  isGoToCheckoutLoading: boolean;
  isAddToCartLoading?: boolean;
  isOnEcom: boolean;
  onBuyNowClick(variant: PricingVariant): void;
  onAddToCartClick(variant: PricingVariant): void;
}

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  variants,
  variantCategoryNameById,
  isVariantSelectionEnabled,
  highlighted,
  buttonDemo,
  recurringPlansExist,
  freeTrialDaysExist,
  validityCyclesExist,
  setupFeesExist,
  taglinesExist,
  anyBenefits,
  expandMobileBenefits,
  isGoToCheckoutLoading,
  isAddToCartLoading,
  isOnEcom,
  onBuyNowClick,
  onAddToCartClick,
}) => {
  const [selectedVariantId, setSelectedVariantId] = useState(variants[0].id!);
  const selectedVariant = variants.find((variant) => variant.id === selectedVariantId) ?? variants[0];
  const settings = useSettings();
  const styles = useStyles();
  const { isMobile } = useEnvironment();
  const shadowStyle = useShadowStyle(highlighted);

  const showCoverImage = styles.get(stylesParams.showImage) || settings.get(settingsParams.showImage);
  const coverImage = planImageToCoverImage(plan.image);
  const hasVariantToggle = isVariantSelectionEnabled && variants.length > 1;
  const showPlanTagline = taglinesExist && styles.get(stylesParams.showTagline);
  const hasTwoDetails = validityCyclesExist && freeTrialDaysExist;
  const descriptionAlignment = alignmentName(styles.get(stylesParams.descriptionAlignment));
  const primaryButtonAlignment = alignmentName(
    styles.get(BUTTON_ALIGNMENT_STYLES_PARAM_BY_BUTTON_STYLE[styles.get(stylesParams.buttonStyle) as ButtonStyle]),
  );
  const secondaryButtonAlignment = alignmentName(styles.get(stylesParams.secondaryButtonAlignment));

  return (
    <li
      data-hook={VIEWER_HOOKS.PLAN_LIST_ITEM}
      className={st(classes.root, { showShadow: shadowStyle.show, highlighted })}
      style={{ [vars.shadowXOffset]: shadowStyle.xOffset + 'px', [vars.shadowYOffset]: shadowStyle.yOffset + 'px' }}
    >
      <Ribbon highlighted={highlighted} />
      <div
        className={st(classes.card, {
          highlighted,
          blendBgColors: !settings.get(settingsParams.wereNewSettingsOpened),
        })}
      >
        {showCoverImage && (
          <div className={classes.coverImageRow}>
            <Image
              data-hook={VIEWER_HOOKS.COVER_IMAGE}
              alt={coverImage?.altText ?? ''}
              src={coverImage?.uri ?? demoImages.default.uri}
              resize={ImageResizeOptions.cover}
              loadingBehavior={ImageLoadingBehaviorOptions.blur}
            />
          </div>
        )}
        <div className={st(classes.top, { textAlignment: descriptionAlignment })}>
          <div className={classes.variantToggleRow}>
            {hasVariantToggle && (
              <VariantToggle
                variants={variants}
                variantCategoryNameById={variantCategoryNameById}
                selectedVariantId={selectedVariant.id!}
                onSelect={(variant) => setSelectedVariantId(variant.id!)}
              />
            )}
          </div>
          <div className={classes.nameRow}>
            <PlanName planId={plan.id!} highlighted={highlighted} name={plan.name ?? ''} />
          </div>
          <div className={classes.priceRow}>
            <Price highlighted={highlighted} price={getVariantPrice(selectedVariant, plan.currency)} />
          </div>
          <div className={classes.recurrenceRow}>
            <Recurrence highlighted={highlighted} variant={selectedVariant} recurringPlansExist={recurringPlansExist} />
          </div>
          <div className={classes.setupFeeRow}>
            {setupFeesExist && (
              <SetupFee plan={plan} variant={selectedVariant} planId={plan.id!} highlighted={highlighted} />
            )}
          </div>
          <div className={classes.taglineRow}>
            {showPlanTagline && (
              <Tagline planId={plan.id!} highlighted={highlighted} description={plan.description ?? ''} />
            )}
          </div>
          <div className={st(classes.durationRow, { hasTwoDetails, mobile: isMobile })}>
            <Validity highlighted={highlighted} variant={selectedVariant} />
            <FreeTrial highlighted={highlighted} variant={selectedVariant} freeTrialDaysExist={freeTrialDaysExist} />
          </div>
          <div className={classes.buttonsRow}>
            <PlanButtons
              plan={plan}
              variant={selectedVariant}
              highlighted={highlighted}
              buttonDemo={buttonDemo}
              isGoToCheckoutLoading={isGoToCheckoutLoading}
              onBuyNowClick={() => onBuyNowClick(selectedVariant)}
              isAddToCartLoading={isAddToCartLoading}
              onAddToCartClick={() => onAddToCartClick(selectedVariant)}
              isOnEcom={isOnEcom}
              displayedButtons={styles.get(stylesParams.displayedButtons)}
              topButton={styles.get(stylesParams.topButton) as TopButton}
              primaryButtonAlignment={primaryButtonAlignment}
              secondaryButtonAlignment={secondaryButtonAlignment}
            />
          </div>
        </div>
        <div className={classes.benefitsRow}>
          <Footer
            benefits={getPlanBenefits(plan)}
            anyBenefits={anyBenefits}
            highlighted={highlighted}
            expandMobileBenefits={expandMobileBenefits}
          />
        </div>
      </div>
    </li>
  );
};
